/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import AuthSlice from '../../azure-b2c/storage';

import { NutritionPromoItem, NutritionPromoResponse, NutritionPromoState } from './types';
import { TDictionary } from '../../../util/types';
import { storageDb } from '../../../services/localforage';

function groupItemsByName(items: NutritionPromoItem[]): { [name: string]: NutritionPromoItem[] } {
  const groupedItems: { [name: string]: NutritionPromoItem[] } = {};

  items.forEach((item) => {
    if (!groupedItems[item.name]) {
      groupedItems[item.name] = [];
    }
    groupedItems[item.name].push(item);
    groupedItems[item.name].sort((a, b) => (a.spread ?? 0) - (b.spread ?? 0));
  });

  return groupedItems;
}

// Fake a false price for testing the error
// const products = data.products.map((e) => {
//   return {
//     ...e,
//     prices: e.prices.map((f) => {
//       return { ...f, price: f.price !== null ? Math.random() * 100 : null }
//     }),
//   }
// })
const cleanNutritionPromoResponse = (data: NutritionPromoResponse): NutritionPromoResponse => {
  const products = data.products.map((e) => {
    return {
      ...e,
      prices: e.prices.filter((location) => location.price !== null),
    };
  });
  const dictionary = groupItemsByName(products);

  return {
    config: data.config,
    products: products,
    dictionary: dictionary,
  };
};

export const getNutritionPromoInfo = createAsyncThunk<NutritionPromoResponse>(
  'nutrition/pricing',
  async () => {
    const request = await apiClient.get<NutritionPromoResponse, AxiosError>(`/promo`);
    return cleanNutritionPromoResponse(request.data);
  },
);

export const nutritionPersistConfig = {
  key: 'nutrition',
  storage: storageDb,
  // serialize: false,
  blacklist: ['getStatus', 'submitStatus'],
};

export const initialState: NutritionPromoState = {
  promo: {
    config: {
      enabled: false,
      startMonth: new Date(),
    },
    products: [],
    dictionary: {},
  },
  getStatus: 'idle',
  submitStatus: 'idle',
};

const NutritionPromoSlice = createSlice({
  name: 'products',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(AuthSlice.actions.setSelectedFarmerAccount, (state) => {
      state.getStatus = 'idle';
    });
    builder.addCase(getNutritionPromoInfo.fulfilled, (state, action) => {
      state.promo = action.payload;
      state.getStatus = 'fulfilled';
    });
    builder.addCase(getNutritionPromoInfo.pending, (state) => {
      state.getStatus = 'pending';
    });
    builder.addCase(getNutritionPromoInfo.rejected, (state) => {
      state.getStatus = 'fulfilled';
    });
  },
  reducers: {},
});

const selectState = (state: RootState) => state;

export const selectPromoEnabled = createSelector(
  selectState,
  (state): boolean => state.nutrition.promo.config.enabled,
);

export const selectPromoConfig = createSelector(
  selectState,
  (state) => state.nutrition.promo.config,
);

export const selectPromoItems = createSelector(
  selectState,
  (state): NutritionPromoItem[] => state.nutrition.promo.products,
);

export const selectPromoDictionary = createSelector(
  selectState,
  (state): TDictionary<NutritionPromoItem[]> => state.nutrition.promo.dictionary,
);

export default NutritionPromoSlice;
